import React from 'react';
import styles from './UploaderPromo.module.css';

// Apple logo — macOS tile.
const AppleIcon = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="#02071a" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.05 20.28c-.98.95-2.05.79-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

// Windows logo — Windows tile.
const WindowsIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path fill="#00A4EF" d="M3 5.1L10.5 4v6.9H3V5.1zM10.5 11.6v6.9L3 17.4v-5.8h7.5zM11.4 3.87L21 2.5v8.4h-9.6V3.87zM21 11.6V20l-9.6-1.37V11.6H21z" />
  </svg>
);

const DOWNLOADS = [
  {
    key: 'windows',
    label: 'Windows',
    sub: 'High-speed transfer',
    href: 'https://apps.microsoft.com/detail/9NM90HDCTCX7',
    icon: <WindowsIcon />,
  },
  {
    key: 'mac',
    label: 'macOS',
    sub: 'Requires macOS 12.1 or newer',
    href: 'https://apps.apple.com/us/app/projector-uploader/id6744290527?mt=12',
    icon: <AppleIcon />,
  },
];

// Small glyphs for the Projector Stream (mobile & TV) tiles.
const AppleMiniIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="#02071a" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.05 20.28c-.98.95-2.05.79-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

const PlayMiniIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="#02071a" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.609 1.814L13.792 12 3.609 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .61-.92zM14.5 12.707l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626c.879.51.879 1.671 0 2.181l-2.808 1.626L15.499 12l2.2-2.491zM6.293 2.472l10.937 6.333L14.928 11.1 6.293 2.472z" />
  </svg>
);

// Roku — monochrome TV glyph to match the other stream tiles.
const RokuMiniIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#02071a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="4" width="20" height="13" rx="2" />
    <path d="M8 21h8" />
    <path d="M12 17v4" />
  </svg>
);

// Projector Stream (the viewer app) — available on mobile & TV.
const STREAM_DOWNLOADS = [
  {
    key: 'ios',
    label: 'iOS',
    href: 'https://apps.apple.com/in/app/projector-stream/id1585850104',
    icon: <AppleMiniIcon />,
  },
  {
    key: 'android',
    label: 'Android',
    href: 'https://play.google.com/store/apps/details?id=com.projectorllc.projectorapp',
    icon: <PlayMiniIcon />,
  },
  {
    key: 'appletv',
    label: 'Apple TV',
    href: 'https://apps.apple.com/in/app/projector-stream/id1585850104',
    icon: <AppleMiniIcon />,
  },
  {
    key: 'googletv',
    label: 'Google TV / Fire TV',
    href: 'https://play.google.com/store/apps/details?id=com.projectorllc.projectorapptv',
    icon: <PlayMiniIcon />,
  },
  {
    key: 'roku',
    label: 'Roku TV',
    href: 'https://channelstore.roku.com/details/0b5b16be5182204ba916edba34f69b20/projector',
    icon: <RokuMiniIcon />,
  },
];

const FEATURES = [
  'Find and upload content from your computer and connected cloud drives',
  'Fast, secure high-speed transfers',
  'Tag, organize and manage your content across devices',
];

// Detect the current desktop OS the same way the projectoruploader.com page does.
const detectDesktopOS = () => {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';
  if (/Windows/i.test(ua)) return 'windows';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'mac';
  return null;
};

export const UploaderPromo = () => {
  const detectedOS = detectDesktopOS();
  // Surface the matching desktop build first so it's the obvious choice.
  const orderedDownloads = detectedOS
    ? [...DOWNLOADS].sort((a, b) => {
      if (a.key === detectedOS) return -1;
      if (b.key === detectedOS) return 1;
      return 0;
    })
    : DOWNLOADS;

  return (
    <div className={styles.promoCard}>
    <span className={styles.eyebrow}>Projector Uploader</span>
    <h2 className={styles.promoTitle}>Install Projector Uploader</h2>
    <p className={styles.promoTagline}>
      Find and upload your content from your computer and connected cloud drives.
    </p>

    <ul className={styles.featureList}>
      {FEATURES.map((feature) => (
        <li key={feature} className={styles.featureItem}>
          <span className={styles.featureBullet}>✓</span>
          <span>{feature}</span>
        </li>
      ))}
    </ul>

    <h3 className={styles.downloadHeading}>Download for desktop</h3>
    <div className={styles.downloadGrid}>
      {orderedDownloads.map((item) => {
        const isCurrent = item.key === detectedOS;
        return (
          <a
            key={item.key}
            className={`${styles.downloadCard} ${isCurrent ? styles.downloadCardActive : ''}`}
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className={styles.downloadIcon}>{item.icon}</span>
            <span className={styles.downloadText}>
              <span className={styles.downloadLabel}>{item.label}</span>
              <span className={styles.downloadSub}>{item.sub}</span>
            </span>
            {isCurrent && (
              <span className={styles.deviceBadge}>Supported on this device</span>
            )}
          </a>
        );
      })}
    </div>

    <h3 className={styles.streamHeading}>Watch on mobile &amp; TV — Projector Stream</h3>
    <div className={styles.streamGrid}>
      {STREAM_DOWNLOADS.map((item) => (
        <a
          key={item.key}
          className={styles.streamCard}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className={styles.streamIcon}>{item.icon}</span>
          <span className={styles.streamLabel}>{item.label}</span>
        </a>
      ))}
    </div>
    </div>
  );
};

export default UploaderPromo;
